import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Select,
  Tag,
  message,
  Popconfirm,
  Typography,
  Button,
  Tooltip,
} from "antd";
import { StopOutlined, ReloadOutlined } from "@ant-design/icons";
import styled from "styled-components";

import { listInviteLinks, revokeInviteLink } from "../api/inviteLinkApi";
import { formatClientName } from "../features/dashai/lib/utils";

const { Text } = Typography;
const { Option } = Select;

// Table chrome matching the API Keys / User-list tables (rounded container,
// #fafafa header, Inter, hairline borders) so the admin tables read as one
// system.
const StyledLinkTable = styled(Table)`
  .ant-table {
    font-family: "Inter", sans-serif;
  }
  .ant-table-container {
    border: 0.5px solid rgba(0, 0, 0, 0.06);
    border-radius: 12px;
    overflow: hidden;
  }
  .ant-table-thead > tr > th {
    background: #fafafa;
    color: #27272a;
    font-family: "Inter", sans-serif;
    font-weight: 500;
    font-size: 0.875rem;
    padding: 0.625rem;
    border-bottom: 0.5px solid rgba(0, 0, 0, 0.06);
  }
  .ant-table-tbody > tr > td {
    font-family: "Inter", sans-serif;
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.85);
    padding: 0.625rem;
    border-bottom: 0.5px solid rgba(0, 0, 0, 0.06);
  }
  .ant-table-tbody > tr:hover > td {
    background: #f8fafc;
  }
  .ant-table-tbody > tr.ant-table-placeholder > td {
    color: #a1a1aa;
  }
  .ant-table-tbody > tr.ant-table-placeholder:hover > td {
    background: transparent;
  }
`;

// Status → pill colour. Only "active" links are still usable; the rest are
// terminal states shown for context.
const STATUS_TAG = {
  active: { color: "green", label: "Active" },
  revoked: { color: "red", label: "Revoked" },
  expired: { color: "default", label: "Expired" },
  exhausted: { color: "orange", label: "Exhausted" },
};

const ROLE_LABEL = {
  super_admin: "Super User",
  admin: "Admin",
  normal_user: "Normal User",
};

const InviteLinksManagementPage = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState(null); // null = all
  const [revoking, setRevoking] = useState(null); // token_prefix in flight

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listInviteLinks();
      setLinks(data.links || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const handleRevoke = async (record) => {
    setRevoking(record.token_prefix);
    try {
      await revokeInviteLink(record.id_client, record.token_prefix);
      message.success("Invite link revoked");
      fetchLinks();
    } catch (err) {
      message.error(err.message);
    } finally {
      setRevoking(null);
    }
  };

  // Workspace filter options derived from the loaded links.
  const clientOptions = useMemo(() => {
    const ids = [...new Set(links.map((l) => l.id_client).filter(Boolean))];
    ids.sort();
    return ids;
  }, [links]);

  const visibleLinks = clientFilter
    ? links.filter((l) => l.id_client === clientFilter)
    : links;

  const columns = [
    {
      title: "Token",
      dataIndex: "token_prefix",
      key: "token_prefix",
      render: (prefix) => (
        <Text code style={{ whiteSpace: "nowrap" }}>
          {prefix}…
        </Text>
      ),
    },
    {
      title: "Workspace",
      dataIndex: "id_client",
      key: "id_client",
      render: (v) => formatClientName(v),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (v) => ROLE_LABEL[v] || v,
    },
    {
      title: "Domain",
      dataIndex: "domain_restriction",
      key: "domain_restriction",
      render: (v) =>
        v ? (
          <Tag style={{ borderRadius: 9999 }}>{v}</Tag>
        ) : (
          <Text type="secondary">Any</Text>
        ),
    },
    {
      title: "Uses",
      key: "uses",
      render: (_, r) => `${r.use_count ?? 0} / ${r.max_uses ?? "∞"}`,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (val) => (val ? new Date(val).toLocaleDateString() : "—"),
    },
    {
      title: "Expires",
      dataIndex: "expires_at",
      key: "expires_at",
      render: (val) => (val ? new Date(val).toLocaleDateString() : "Never"),
    },
    {
      title: "Created by",
      dataIndex: "created_by",
      key: "created_by",
      render: (v) => (
        <Text type="secondary" style={{ fontSize: "0.75rem" }}>
          {v || "—"}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const tag = STATUS_TAG[status] || { color: "default", label: status };
        return (
          <Tag color={tag.color} style={{ borderRadius: 9999 }}>
            {tag.label}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, record) => {
        // Only an active link can be revoked; the rest are already terminal.
        const canRevoke = record.status === "active";
        const button = (
          <Button
            type="text"
            danger
            icon={<StopOutlined />}
            size="small"
            disabled={!canRevoke}
            loading={revoking === record.token_prefix}
            aria-label="Revoke invite link"
          />
        );
        // A disabled antd Button swallows hover events, so a Tooltip/Popconfirm
        // wrapping it directly never fires. Wrap the disabled button in a span
        // so the explanatory tooltip still shows on hover.
        if (!canRevoke) {
          return (
            <Tooltip title="Only active links can be revoked">
              <span>{button}</span>
            </Tooltip>
          );
        }
        return (
          <Popconfirm
            title="Revoke this invite link?"
            description="Anyone still holding the link can no longer register with it. This cannot be undone."
            onConfirm={() => handleRevoke(record)}
            okText="Revoke"
            okType="danger"
          >
            <Tooltip title="Revoke permanently">{button}</Tooltip>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    // Offset the fixed 240px sidebar, matching the other admin pages.
    <div
      className="ml-[240px] w-[calc(100%-240px)] min-h-[100vh]"
      style={{ padding: "1.5rem" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              fontFamily: "Satoshi",
              fontWeight: 700,
              fontSize: "1.5rem",
            }}
          >
            Invite Links
          </div>
          <Select
            allowClear
            showSearch
            style={{ minWidth: 220 }}
            placeholder="All workspaces"
            value={clientFilter}
            onChange={(v) => setClientFilter(v ?? null)}
            optionFilterProp="children"
          >
            {clientOptions.map((c) => (
              <Option key={c} value={c}>
                {formatClientName(c)}
              </Option>
            ))}
          </Select>
        </div>
        <Tooltip title="Refresh">
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchLinks}
            aria-label="Refresh invite links"
          />
        </Tooltip>
      </div>

      <StyledLinkTable
        dataSource={visibleLinks}
        columns={columns}
        rowKey={(r) => `${r.id_client}:${r.token_prefix}`}
        loading={loading}
        pagination={{ pageSize: 20 }}
        locale={{
          emptyText: loading ? null : "No invite links for this scope",
        }}
      />
    </div>
  );
};

export default InviteLinksManagementPage;
